import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarsArrowDownIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageOrderModal } from 'components/Modals/PageOrder'
import { SaveChanges } from 'components/SaveChanges'
import type { InputType } from 'config'
import {
  buyPricingFields,
  investorCommitmentFields,
  originatorLockFields,
  sellPricingFields,
} from 'pages/Loan/SecondaryMarketing/constant'
import { useEffect, useMemo, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { getAdminConfig, setAdminConfig } from 'services'
import { Tooltip } from 'stories/components'
import { prompt } from 'utils'

import {
  defaultSmFieldOrder,
  emptySmScreenConfig,
  getSmFieldTitle,
  getSmSectionTitle,
  ISmScreenConfig,
  isSmFieldVisible,
  isSmSectionVisible,
  normalizeSmScreenConfig,
  orderedFlatKeys,
  orderedSmSections,
  orderedTwoColumnKeys,
  SM_COLUMN_LABELS,
  SM_SCREEN_SECTIONS,
  SmScreenSectionKey,
  SmTwoColumnGroup,
} from './types'

type OrderTarget =
  | { kind: 'section' }
  | { kind: 'twoColumn'; section: 'originatorLock' | 'investorCommitment'; group: SmTwoColumnGroup }
  | { kind: 'flat'; section: 'buyPricing' | 'sellPricing' }

const CONFIG_KEY = 'secondaryMarketingScreenConfig'

const twoColumnMaps = {
  originatorLock: originatorLockFields,
  investorCommitment: investorCommitmentFields,
} as const

const flatMaps = {
  buyPricing: buyPricingFields,
  sellPricing: sellPricingFields,
} as const

export const SecondaryMarketingScreenFieldsConfig = () => {
  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [data, setData] = useState<ISmScreenConfig>(emptySmScreenConfig())
  const [orderTarget, setOrderTarget] = useState<OrderTarget | null>(null)

  useEffect(() => {
    setLoading(true)
    getAdminConfig(CONFIG_KEY)
      .then((res) => setData(normalizeSmScreenConfig(res)))
      .finally(() => setLoading(false))
  }, [])

  const sectionList = useMemo(() => {
    const byKey = Object.fromEntries(SM_SCREEN_SECTIONS.map((s) => [s.key, s]))
    return orderedSmSections(data)
      .map((key) => byKey[key])
      .filter(Boolean)
  }, [data])

  const markChanged = (next: ISmScreenConfig) => {
    setData(next)
    setChanged(true)
  }

  const onToggleSectionVisible = (key: SmScreenSectionKey, current: boolean) => {
    const next = cloneDeep(data)
    if (!next.sections) next.sections = {}
    if (!next.sections[key]) next.sections[key] = {}
    next.sections[key].visible = !current
    markChanged(next)
  }

  const onRenameSection = async (key: SmScreenSectionKey, currentTitle: string) => {
    const value = (await prompt('Enter new section title', { value: currentTitle })) as any
    if (value === false || value === undefined) return
    const next = cloneDeep(data)
    if (!next.sections) next.sections = {}
    if (!next.sections[key]) next.sections[key] = {}
    const trimmed = String(value || '').trim()
    if (!trimmed) delete next.sections[key].title
    else next.sections[key].title = trimmed
    markChanged(next)
  }

  const onMoveSection = (key: SmScreenSectionKey, direction: -1 | 1) => {
    const order = orderedSmSections(data)
    const index = order.indexOf(key)
    const swapWith = index + direction
    if (index < 0 || swapWith < 0 || swapWith >= order.length) return
    const nextOrder = [...order]
    ;[nextOrder[index], nextOrder[swapWith]] = [nextOrder[swapWith], nextOrder[index]]
    markChanged({ ...cloneDeep(data), sectionOrder: nextOrder })
  }

  const onToggleFieldVisible = (fieldKey: string, current: boolean) => {
    const next = cloneDeep(data)
    if (!next.fields) next.fields = {}
    if (!next.fields[fieldKey]) next.fields[fieldKey] = {}
    next.fields[fieldKey].visible = !current
    markChanged(next)
  }

  const onRenameField = async (fieldKey: string, currentTitle: string) => {
    const value = (await prompt('Enter new field title', { value: currentTitle })) as any
    if (value === false || value === undefined) return
    const next = cloneDeep(data)
    if (!next.fields) next.fields = {}
    if (!next.fields[fieldKey]) next.fields[fieldKey] = {}
    const trimmed = String(value || '').trim()
    if (!trimmed) delete next.fields[fieldKey].title
    else next.fields[fieldKey].title = trimmed
    markChanged(next)
  }

  const orderModalInputs = useMemo(() => {
    if (!orderTarget) return []
    if (orderTarget.kind === 'section') {
      return sectionList.map((s) => ({
        title: getSmSectionTitle(data, s.key, s.title),
        key: s.key,
      }))
    }
    if (orderTarget.kind === 'twoColumn') {
      const defaults = twoColumnMaps[orderTarget.section][orderTarget.group]
      return orderedTwoColumnKeys(data, orderTarget.section, orderTarget.group, defaults).map((key) => ({
        title: getSmFieldTitle(data, key, defaults[key]?.title || key),
        key,
      }))
    }
    const defaults = flatMaps[orderTarget.section]
    return orderedFlatKeys(data, orderTarget.section, defaults).map((key) => ({
      title: getSmFieldTitle(data, key, defaults[key]?.title || key),
      key,
    }))
  }, [orderTarget, sectionList, data])

  const onCloseOrderModal = (newInputs: { title: string; key?: string }[] | null) => {
    const target = orderTarget
    setOrderTarget(null)
    if (!newInputs || !target) return

    const next = cloneDeep(data)
    if (target.kind === 'section') {
      const titles = sectionList.map((s) => getSmSectionTitle(data, s.key, s.title))
      next.sectionOrder = newInputs
        .map((item) => {
          const fromKey = (item as any).key as SmScreenSectionKey | undefined
          if (fromKey) return fromKey
          const idx = titles.indexOf(item.title)
          return sectionList[idx]?.key
        })
        .filter(Boolean) as SmScreenSectionKey[]
      markChanged(next)
      return
    }

    if (!next.fieldOrder) next.fieldOrder = {}
    const defaults = defaultSmFieldOrder()
    const resolveKeys = (items: { title: string; key?: string }[], fallbackTitles: string[], fallbackKeys: string[]) =>
      items
        .map((item) => {
          if ((item as any).key) return (item as any).key as string
          const idx = fallbackTitles.indexOf(item.title)
          return fallbackKeys[idx]
        })
        .filter(Boolean)

    if (target.kind === 'twoColumn') {
      const defs = twoColumnMaps[target.section][target.group]
      const keys = orderedTwoColumnKeys(data, target.section, target.group, defs)
      const titles = keys.map((k) => getSmFieldTitle(data, k, defs[k]?.title || k))
      if (!next.fieldOrder[target.section]) next.fieldOrder[target.section] = { ...defaults[target.section] }
      ;(next.fieldOrder[target.section] as any)[target.group] = resolveKeys(newInputs, titles, keys)
      markChanged(next)
      return
    }

    const defs = flatMaps[target.section]
    const keys = orderedFlatKeys(data, target.section, defs)
    const titles = keys.map((k) => getSmFieldTitle(data, k, defs[k]?.title || k))
    next.fieldOrder[target.section] = resolveKeys(newInputs, titles, keys)
    markChanged(next)
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig(CONFIG_KEY, data)
    setLoading(false)
    setChanged(false)
  }

  const renderFieldRow = (fieldKey: string, fallbackTitle: string) => {
    const visible = isSmFieldVisible(data, fieldKey)
    const title = getSmFieldTitle(data, fieldKey, fallbackTitle)
    const VisibleIcon = visible ? EyeIcon : EyeSlashIcon
    return (
      <div className="flex justify-between items-center py-1 pl-2" key={fieldKey}>
        <p className={`text-sm ${visible ? '' : 'line-through opacity-60'}`}>{title}</p>
        <div className="flex gap-2 items-center">
          <Tooltip message={visible ? 'Hide field' : 'Show field'}>
            <span className="btn-icon" onClick={() => onToggleFieldVisible(fieldKey, visible)}>
              <VisibleIcon className="w-4 h-4" />
            </span>
          </Tooltip>
          <Tooltip message="Rename field">
            <span className="btn-icon" onClick={() => onRenameField(fieldKey, title)}>
              <PencilSquareIcon className="w-4 h-4" />
            </span>
          </Tooltip>
        </div>
      </div>
    )
  }

  const renderTwoColumnSection = (section: 'originatorLock' | 'investorCommitment') => {
    const map = twoColumnMaps[section]
    return (['leftGroup', 'rightGroup'] as SmTwoColumnGroup[]).map((group) => {
      const defaults = map[group]
      const keys = orderedTwoColumnKeys(data, section, group, defaults)
      return (
        <div className="md:col-span-1 border rounded p-3" key={`${section}-${group}`}>
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-semibold text-gray-700">{SM_COLUMN_LABELS[group]}</p>
            <Tooltip message="Reorder fields">
              <span className="btn-icon" onClick={() => setOrderTarget({ kind: 'twoColumn', section, group })}>
                <BarsArrowDownIcon className="w-4 h-4" />
              </span>
            </Tooltip>
          </div>
          {keys.map((key) => renderFieldRow(key, defaults[key]?.title || key))}
        </div>
      )
    })
  }

  const renderFlatSection = (section: 'buyPricing' | 'sellPricing', label: string) => {
    const defaults = flatMaps[section]
    const keys = orderedFlatKeys(data, section, defaults)
    return (
      <div className="md:col-span-1 border rounded p-3" key={section}>
        <div className="flex justify-between items-center mb-2">
          <p className="text-sm font-semibold text-gray-700">{label}</p>
          <Tooltip message="Reorder fields">
            <span className="btn-icon" onClick={() => setOrderTarget({ kind: 'flat', section })}>
              <BarsArrowDownIcon className="w-4 h-4" />
            </span>
          </Tooltip>
        </div>
        {keys.map((key) => renderFieldRow(key, (defaults[key] as InputType)?.title || key))}
      </div>
    )
  }

  return (
    <div className="relative mt-4">
      <LayoutLoading show={isLoading} />
      <Prompt when={!!isChanged} message="You have unsaved changes. Are you sure you want to leave?" />
      <SaveChanges show={isChanged} onSave={onSubmit} />

      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-600">
          Reorder Secondary Marketing sections and fields, rename labels, or hide fields from the loan screen.
        </p>
        <Tooltip message="Reorder sections">
          <span className="btn-icon" onClick={() => setOrderTarget({ kind: 'section' })}>
            <BarsArrowDownIcon className="w-5 h-5" />
          </span>
        </Tooltip>
      </div>

      <div className="space-y-4">
        {sectionList.map((section, index) => {
          const visible = isSmSectionVisible(data, section.key)
          const title = getSmSectionTitle(data, section.key, section.title)
          const VisibleIcon = visible ? EyeIcon : EyeSlashIcon

          return (
            <div className={`border rounded ${visible ? '' : 'opacity-70'}`} key={section.key}>
              <div className="bg-gray-100 px-3 py-2 flex justify-between items-center gap-2">
                <p className={`font-semibold text-sm ${visible ? '' : 'line-through'}`}>
                  {index + 1}. {title}
                </p>
                <div className="flex gap-2 items-center">
                  <Tooltip message={visible ? 'Hide section' : 'Show section'}>
                    <span className="btn-icon" onClick={() => onToggleSectionVisible(section.key, visible)}>
                      <VisibleIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                  <Tooltip message="Rename section">
                    <span className="btn-icon" onClick={() => onRenameSection(section.key, title)}>
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                  <Tooltip message="Move up">
                    <span className="btn-icon" onClick={() => onMoveSection(section.key, -1)}>
                      <ArrowUpIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                  <Tooltip message="Move down">
                    <span className="btn-icon" onClick={() => onMoveSection(section.key, 1)}>
                      <ArrowDownIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </div>
              </div>

              <div className="p-3">
                {section.key === 'originatorLock' && (
                  <div className="grid md:grid-cols-2 gap-3">{renderTwoColumnSection('originatorLock')}</div>
                )}
                {section.key === 'investorCommitment' && (
                  <div className="grid md:grid-cols-2 gap-3">{renderTwoColumnSection('investorCommitment')}</div>
                )}
                {section.key === 'markup' && (
                  <p className="text-sm text-gray-500">
                    Markup is a table section. Use the section controls above to rename, hide, or reorder it.
                  </p>
                )}
                {section.key === 'pricing' && (
                  <div className="grid md:grid-cols-2 gap-3">
                    {renderFlatSection('buyPricing', 'Buy Side')}
                    {renderFlatSection('sellPricing', 'Sell Side')}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {orderTarget && (
        <PageOrderModal
          key={`${orderTarget.kind}-${'section' in orderTarget ? (orderTarget as any).section : ''}-${
            'group' in orderTarget ? (orderTarget as any).group : ''
          }`}
          inputs={orderModalInputs}
          onClose={onCloseOrderModal}
        />
      )}
    </div>
  )
}
